/* iSpeedToClose-managed Toll-Free numbers (877 / 800 / 888 / etc.).
 *
 * Surfaces in Settings → Twilio, BELOW ManagedNumbersCard. Distinct lane
 * because Toll-Free Verification is mandatory for SMS (Twilio error 30032
 * fires otherwise). Voice works instantly upon purchase.
 *
 * Flow:
 *   1. Pick a toll-free prefix (default 877) → search.
 *   2. Buy — number lands in same subaccount + MessagingService.
 *   3. Verify — modal form; submits to Twilio Trust Hub.
 *   4. Refresh — poll status until TWILIO_APPROVED.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Phone, Search, Loader2, Check, ShieldCheck, ShieldAlert,
  Sparkles, RefreshCw, ChevronRight, PhoneIncoming, MessageSquare,
  Image as ImageIcon,
} from "lucide-react";

const STATUS_COPY = {
  unsubmitted:     { label: "Not Verified",   tone: "amber",   icon: ShieldAlert },
  pending_review:  { label: "Pending Review", tone: "blue",    icon: Loader2 },
  in_review:       { label: "In Review",      tone: "blue",    icon: Loader2 },
  twilio_approved: { label: "Verified",       tone: "emerald", icon: ShieldCheck },
  twilio_rejected: { label: "Rejected",       tone: "rose",    icon: ShieldAlert },
};

function StatusBadge({ status }) {
  const info = STATUS_COPY[status] || STATUS_COPY.unsubmitted;
  const Icon = info.icon;
  const cls = {
    amber:   "bg-amber-100 text-amber-800 border-amber-200",
    blue:    "bg-blue-100 text-blue-800 border-blue-200",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
    rose:    "bg-rose-100 text-rose-800 border-rose-200",
  }[info.tone];
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}
          data-testid={`tf-status-${status}`}>
      <Icon size={9} className={status.endsWith("review") ? "animate-spin" : ""} />
      {info.label}
    </span>
  );
}

function CapabilityChip({ enabled, label, Icon }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
      enabled ? "bg-emerald-100 text-emerald-800 border-emerald-200"
              : "bg-stone-100 text-stone-500 border-stone-200"}`}>
      <Icon size={9}/> {label}
    </span>
  );
}

// ─────────────────── Verification Modal ───────────────────

function VerifyModal({ number, onClose, onDone }) {
  const [form, setForm] = useState({
    business_name: "", business_website: "https://",
    first_name: "", last_name: "", email: "",
    phone: "", address: "", city: "", state: "", zip_code: "",
    use_case_summary: "",
    message_sample: "Hi {Name}, thanks for reaching out about your property. Reply STOP to opt out.",
    opt_in_type: "WEB_FORM", message_volume: "1,000",
    opt_in_image_urls: [""],
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      const payload = { ...form,
        opt_in_image_urls: form.opt_in_image_urls.filter(Boolean) };
      const { data } = await api.post(
        `/twilio-tollfree/${number.twilio_phone_sid}/verify`, payload);
      toast.success("Verification submitted — carriers take 1-3 business days.");
      onDone(data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="tf-verify-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-[var(--line)] sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-extrabold text-lg">
                Verify {number.phone_number}
              </div>
              <div className="text-xs text-[var(--ink-2)] mt-0.5">
                Required by US carriers before SMS. Voice works now, this
                unlocks messaging.
              </div>
            </div>
            <button type="button" onClick={onClose}
                    className="text-slate-500 hover:text-slate-900 text-2xl leading-none"
                    data-testid="tf-verify-close">×</button>
          </div>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
            <b>Important:</b> Twilio reviewers manually check your website&apos;s
            Privacy Policy and opt-in screenshots. If the site 404s, requires
            login, or omits an &ldquo;SMS consent won&apos;t be shared&rdquo; clause,
            verification will be <b>rejected</b>.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Business name" required
              value={form.business_name} onChange={set("business_name")}
              testid="tf-verify-business-name"/>
            <FormField label="Business website" required
              value={form.business_website} onChange={set("business_website")}
              testid="tf-verify-website"/>
            <FormField label="Contact first name" required
              value={form.first_name} onChange={set("first_name")}
              testid="tf-verify-first-name"/>
            <FormField label="Contact last name" required
              value={form.last_name} onChange={set("last_name")}
              testid="tf-verify-last-name"/>
            <FormField label="Email" required type="email"
              value={form.email} onChange={set("email")}
              testid="tf-verify-email"/>
            <FormField label="Business phone (E.164)" required
              placeholder="+13125551234"
              value={form.phone} onChange={set("phone")}
              testid="tf-verify-phone"/>
            <FormField label="Street address" required className="col-span-2"
              value={form.address} onChange={set("address")}
              testid="tf-verify-address"/>
            <FormField label="City" required
              value={form.city} onChange={set("city")}
              testid="tf-verify-city"/>
            <FormField label="State" required placeholder="CA"
              value={form.state} onChange={set("state")}
              testid="tf-verify-state"/>
            <FormField label="ZIP" required
              value={form.zip_code} onChange={set("zip_code")}
              testid="tf-verify-zip"/>

            <div className="col-span-2">
              <label className="block text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold mb-1">
                Use-case summary (min 40 chars) <span className="text-rose-500">*</span>
              </label>
              <textarea rows={3} value={form.use_case_summary}
                        onChange={set("use_case_summary")}
                        className="input w-full text-sm"
                        placeholder="e.g. Send appointment confirmations, closing reminders, and offer updates to consenting real-estate sellers who submitted our web form."
                        data-testid="tf-verify-use-case" required/>
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold mb-1">
                Sample message <span className="text-rose-500">*</span>
              </label>
              <textarea rows={2} value={form.message_sample}
                        onChange={set("message_sample")}
                        className="input w-full text-sm font-mono"
                        data-testid="tf-verify-sample" required/>
              <div className="text-[10px] text-[var(--ink-2)] mt-1">
                Use placeholders like {"{Name}"}. NO real PII.
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold mb-1">
                Opt-in type
              </label>
              <select value={form.opt_in_type} onChange={set("opt_in_type")}
                       className="input w-full text-sm"
                       data-testid="tf-verify-opt-in-type">
                <option value="WEB_FORM">Web form</option>
                <option value="VERBAL">Verbal</option>
                <option value="PAPER_FORM">Paper form</option>
                <option value="VIA_TEXT">Via text</option>
                <option value="MOBILE_QR_CODE">Mobile QR code</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold mb-1">
                Monthly message volume
              </label>
              <select value={form.message_volume} onChange={set("message_volume")}
                       className="input w-full text-sm"
                       data-testid="tf-verify-volume">
                {["10","100","1,000","10,000","100,000","250,000","500,000","750,000","1,000,000","5,000,000","10,000,000+"].map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold mb-1">
                Opt-in screenshot URL (must be publicly accessible)
              </label>
              <input type="url" value={form.opt_in_image_urls[0]}
                     onChange={(e) => setForm((f) => ({...f, opt_in_image_urls: [e.target.value]}))}
                     className="input w-full text-sm"
                     placeholder="https://your-site.com/opt-in.png"
                     data-testid="tf-verify-opt-in-url"/>
            </div>
          </div>

          {err && (
            <div className="rounded-md bg-rose-50 border border-rose-200 p-2.5 text-xs text-rose-900"
                  data-testid="tf-verify-error">{err}</div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--line)]">
            <button type="button" onClick={onClose}
                    className="px-4 py-2 text-sm font-semibold rounded-lg border border-[var(--line)] hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
                    className="bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5"
                    data-testid="tf-verify-submit">
              {submitting ? <Loader2 size={14} className="animate-spin"/> : <ShieldCheck size={14}/>}
              Submit for Verification
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FormField({ label, required, value, onChange, testid, className = "",
                     type = "text", placeholder }) {
  return (
    <div className={className}>
      <label className="block text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold mb-1">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <input type={type} value={value} onChange={onChange} required={required}
             placeholder={placeholder} data-testid={testid}
             className="input w-full text-sm"/>
    </div>
  );
}

// ─────────────────── Main Card ───────────────────

export default function TollFreeNumbersCard() {
  const [configured, setConfigured] = useState(null);
  const [prefixes, setPrefixes] = useState({ prefixes: [], default: "877" });
  const [selectedPrefix, setSelectedPrefix] = useState("877");
  const [containsQ, setContainsQ] = useState("");
  const [results, setResults] = useState([]);
  const [owned, setOwned] = useState([]);
  const [searching, setSearching] = useState(false);
  const [buying, setBuying] = useState(null);
  const [refreshing, setRefreshing] = useState(null);
  const [verifyingNumber, setVerifyingNumber] = useState(null);

  const loadOwned = async () => {
    try {
      const { data } = await api.get("/twilio-tollfree/numbers");
      setOwned(data?.items || []);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 402 || code === 403) setConfigured(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [cfgRes, prefRes] = await Promise.allSettled([
          api.get("/twilio-tollfree/configured"),
          api.get("/twilio-tollfree/prefixes"),
        ]);
        const cfg = cfgRes.status === "fulfilled" && !!cfgRes.value.data?.configured;
        setConfigured(cfg);
        if (prefRes.status === "fulfilled") {
          setPrefixes(prefRes.value.data);
          setSelectedPrefix(prefRes.value.data?.default || "877");
        }
        if (cfg) await loadOwned();
      } catch {
        setConfigured(false);
      }
    })();
  }, []);

  const search = async (e) => {
    e?.preventDefault?.();
    setSearching(true); setResults([]);
    try {
      const { data } = await api.get("/twilio-tollfree/search", {
        params: { prefix: selectedPrefix,
                   contains: containsQ || undefined, limit: 10 },
      });
      setResults(data?.results || []);
      if (!data?.results?.length) {
        toast.info(`No ${selectedPrefix} numbers available right now. Try another prefix.`);
      }
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Search failed.");
    } finally {
      setSearching(false);
    }
  };

  const buy = async (phoneNumber) => {
    if (!window.confirm(
      `Purchase ${phoneNumber} for $5/mo?\n\nVoice works instantly. SMS is ` +
      `blocked until you complete Toll-Free Verification (1-3 business days).`
    )) return;
    setBuying(phoneNumber);
    try {
      await api.post("/twilio-tollfree/purchase", { phone_number: phoneNumber });
      toast.success(`${phoneNumber} is yours — verify next to unlock SMS.`);
      setResults((r) => r.filter((n) => n.phone_number !== phoneNumber));
      await loadOwned();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Purchase failed.");
    } finally {
      setBuying(null);
    }
  };

  const refreshStatus = async (item) => {
    setRefreshing(item.twilio_phone_sid);
    try {
      await api.post(`/twilio-tollfree/${item.twilio_phone_sid}/verification/refresh`);
      toast.success("Status refreshed.");
      await loadOwned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed.");
    } finally {
      setRefreshing(null);
    }
  };

  const verifiedCount = useMemo(
    () => owned.filter((n) => n.verification?.status === "twilio_approved").length,
    [owned]);

  if (configured === null) return null;

  if (configured === false) {
    return (
      <div className="bg-white rounded-xl border border-[var(--line)] p-5 mt-4"
            data-testid="toll-free-card-coming-soon">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 border border-indigo-200 flex items-center justify-center shrink-0">
            <Sparkles size={18} className="text-indigo-600"/>
          </div>
          <div>
            <div className="font-display font-extrabold text-lg leading-tight">
              iSpeedToClose Toll-Free Numbers (877 · 800 · 888)
            </div>
            <div className="text-xs text-[var(--ink-2)] mt-1 max-w-md">
              National toll-free numbers for scaled outbound outreach.
              Coming soon.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-[var(--line)] p-5 mt-4"
          data-testid="toll-free-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-indigo-50 border border-indigo-200 flex items-center justify-center shrink-0">
          <Phone size={18} className="text-indigo-600"/>
        </div>
        <div className="flex-1">
          <div className="font-display font-extrabold text-lg leading-tight">
            iSpeedToClose Toll-Free Numbers
          </div>
          <div className="text-xs text-[var(--ink-2)] mt-0.5">
            National reach on <b>877 · 800 · 888 · 866 · 855 · 844 · 833</b>.
            $5/mo. Voice is instant — SMS unlocks after Toll-Free Verification.
          </div>
        </div>
        {owned.length > 0 && (
          <span className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold whitespace-nowrap"
                data-testid="tf-verified-count">
            {verifiedCount}/{owned.length} verified
          </span>
        )}
      </div>

      {/* Owned numbers */}
      {owned.length > 0 && (
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold mb-2">
            Your toll-free numbers
          </div>
          <div className="space-y-2">
            {owned.map((n) => {
              const status = n.verification?.status || "unsubmitted";
              return (
                <div key={n.twilio_phone_sid}
                      className="border border-[var(--line)] rounded-lg px-3 py-2"
                      data-testid={`tf-owned-${n.twilio_phone_sid}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-semibold">{n.phone_number}</span>
                        <StatusBadge status={status}/>
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <CapabilityChip enabled={!!n.capabilities?.voice} label="Voice" Icon={PhoneIncoming}/>
                        <CapabilityChip enabled={!!n.capabilities?.sms && status === "twilio_approved"} label="SMS" Icon={MessageSquare}/>
                        <CapabilityChip enabled={!!n.capabilities?.mms && status === "twilio_approved"} label="MMS" Icon={ImageIcon}/>
                        <span className="text-[10px] text-[var(--ink-2)] ml-2">${n.monthly_price_usd}/mo</span>
                      </div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      {status === "unsubmitted" || status === "twilio_rejected" ? (
                        <button type="button" onClick={() => setVerifyingNumber(n)}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-2.5 py-1 rounded inline-flex items-center gap-1"
                                data-testid={`tf-verify-btn-${n.twilio_phone_sid}`}>
                          <ShieldCheck size={11}/> {status === "twilio_rejected" ? "Resubmit" : "Verify"}
                        </button>
                      ) : (
                        <button type="button" onClick={() => refreshStatus(n)}
                                disabled={refreshing === n.twilio_phone_sid}
                                className="border border-[var(--line)] hover:bg-slate-50 text-xs font-semibold px-2.5 py-1 rounded inline-flex items-center gap-1"
                                data-testid={`tf-refresh-btn-${n.twilio_phone_sid}`}>
                          {refreshing === n.twilio_phone_sid
                            ? <Loader2 size={11} className="animate-spin"/>
                            : <RefreshCw size={11}/>} Refresh
                        </button>
                      )}
                    </div>
                  </div>
                  {status === "twilio_rejected" && n.verification?.rejection_reason && (
                    <div className="mt-2 rounded bg-rose-50 border border-rose-200 p-2 text-[11px] text-rose-900">
                      <b>Rejection:</b> {n.verification.rejection_reason}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search */}
      <form onSubmit={search} className="space-y-2 mb-3" data-testid="tf-search-form">
        <div className="flex gap-2 flex-wrap items-end">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold mb-1">
              Prefix
            </label>
            <select value={selectedPrefix}
                     onChange={(e) => setSelectedPrefix(e.target.value)}
                     className="input text-sm"
                     data-testid="tf-prefix-select">
              {prefixes.prefixes.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold mb-1">
              Contains (optional)
            </label>
            <input value={containsQ}
                    onChange={(e) => setContainsQ(e.target.value.toUpperCase().slice(0, 10))}
                    placeholder="CLOSE or 4653 (letters↔digits)"
                    className="input w-full text-sm"
                    data-testid="tf-contains-input"/>
          </div>
          <button type="submit" disabled={searching}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5"
                  data-testid="tf-search-submit">
            {searching ? <Loader2 size={14} className="animate-spin"/> : <Search size={14}/>}
            Search
          </button>
        </div>
      </form>

      {/* Search results */}
      {results.length > 0 && (
        <div className="space-y-2" data-testid="tf-search-results">
          {results.map((n) => (
            <div key={n.phone_number}
                  className="border border-[var(--line)] rounded-lg px-3 py-2 flex items-center justify-between gap-3"
                  data-testid={`tf-result-${n.phone_number}`}>
              <div className="min-w-0 flex-1">
                <div className="font-mono text-sm font-semibold">{n.phone_number}</div>
                <div className="text-xs text-[var(--ink-2)]">{n.friendly_name || "Toll-free"}</div>
                <div className="flex items-center gap-1 mt-1">
                  <CapabilityChip enabled={!!n.capabilities?.voice} label="Voice" Icon={PhoneIncoming}/>
                  <CapabilityChip enabled={!!n.capabilities?.sms} label="SMS" Icon={MessageSquare}/>
                  <CapabilityChip enabled={!!n.capabilities?.mms} label="MMS" Icon={ImageIcon}/>
                </div>
              </div>
              <button type="button" onClick={() => buy(n.phone_number)}
                      disabled={buying === n.phone_number}
                      className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5"
                      data-testid={`tf-buy-${n.phone_number}`}>
                {buying === n.phone_number
                  ? <Loader2 size={12} className="animate-spin"/>
                  : <Check size={12}/>}
                Buy $5/mo
              </button>
            </div>
          ))}
        </div>
      )}

      {results.length === 0 && !searching && owned.length === 0 && (
        <div className="rounded-md bg-slate-50 border border-[var(--line)] p-3 text-xs text-[var(--ink-2)]">
          <ChevronRight size={11} className="inline"/>{" "}
          Select a prefix (877 by default) and click Search to find an
          available number. Toll-Free Verification is required after
          purchase for SMS to work.
        </div>
      )}

      {/* Verification Modal */}
      {verifyingNumber && (
        <VerifyModal number={verifyingNumber}
                      onClose={() => setVerifyingNumber(null)}
                      onDone={async () => {
                        setVerifyingNumber(null);
                        await loadOwned();
                      }}/>
      )}
    </div>
  );
}
